import { Box, Rating, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import ImageWithLoader from "../ImageWithLoader";
import VerifiedBadge from "../VerifiedBadge";
import ConvertedPrice from "./ConvertedPrice";
import { resolveImageUri } from "../../utility/constants";
import { formatCityStateCountry } from "../../utility/addressUtils";
import {
  buildProductPath,
  formatProductPriceLabel,
  getProductPriceBounds,
  isPriceHidden,
  isProductNegotiable,
} from "../../utility/utilityFunctions";

const DELIVERY_METHOD_ICONS = {
  PICKUP: <StorefrontOutlinedIcon sx={{ fontSize: 13 }} />,
  DELIVERY: <LocalShippingOutlinedIcon sx={{ fontSize: 13 }} />,
  SERVICE: <WorkOutlineOutlinedIcon sx={{ fontSize: 13 }} />,
  DIGITAL: <CloudDownloadOutlinedIcon sx={{ fontSize: 13 }} />,
};

const DELIVERY_METHOD_LABELS = {
  PICKUP: "Pickup",
  DELIVERY: "Delivery",
  SERVICE: "Service",
  DIGITAL: "Digital",
};

// `roomy` is for grids with wider cards (the home feed's 3 columns): a shorter
// image, more space and larger text from the md breakpoint up. Below that the
// cards are too narrow for it, so they stay compact.
const ProductCard = ({ product, showShop = true, roomy = false }) => {
  const sized = (compact, large) =>
    roomy ? { xs: compact, md: large } : compact;
  const metaFontSize = sized(11, 13);
  const metaIconSize = sized(13, 15);
  const imageUri = product.images?.[0]
    ? resolveImageUri(product.images[0])
    : null;
  const shop = product.shop;
  const locationText = showShop ? formatCityStateCountry(shop?.address) : "";
  const deliveryMethods = (product.deliveryMethods || []).filter(
    (method) => DELIVERY_METHOD_ICONS[method],
  );

  return (
    <Box
      component={Link}
      to={buildProductPath(product)}
      sx={{
        display: "block",
        textDecoration: "none",
        color: "inherit",
        border: "1px solid #e5e7eb",
        borderRadius: 2,
        overflow: "hidden",
        height: "100%",
        "&:hover": {
          borderColor: "#046287",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        },
        transition: "border-color 0.15s, box-shadow 0.15s",
      }}
    >
      <Box
        sx={{
          width: "100%",
          aspectRatio: sized("1 / 1", "10 / 9"),
          bgcolor: "#f5f5f5",
          overflow: "hidden",
        }}
      >
        {imageUri ? (
          <ImageWithLoader src={imageUri} alt={product.productName} />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary" fontSize={12}>
              No image
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ p: sized(1.5, 2) }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            fontSize: roomy ? { md: 16 } : undefined,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mb: sized(0.25, 0.75),
          }}
        >
          {product.productName}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: sized(1, 1.5),
            mb: sized(0.5, 1),
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            color="#046287"
            sx={{ fontSize: roomy ? { md: 16 } : undefined }}
          >
            {formatProductPriceLabel(product)}
          </Typography>
          {isProductNegotiable(product) && !isPriceHidden(product) && (
            <Typography variant="caption" fontWeight={600} color="success.dark">
              Open to offers
            </Typography>
          )}
          {deliveryMethods.length > 0 && (
            // Icons only (matching mobile); labels live in the aria-label and
            // per-icon hover titles.
            <Box
              role="img"
              aria-label={deliveryMethods
                .map((method) => DELIVERY_METHOD_LABELS[method] || method)
                .join(", ")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "text.secondary",
                "& svg": { fontSize: metaIconSize },
              }}
            >
              {deliveryMethods.map((method) => (
                <Box
                  key={method}
                  component="span"
                  title={DELIVERY_METHOD_LABELS[method] || method}
                  sx={{ display: "flex" }}
                >
                  {DELIVERY_METHOD_ICONS[method]}
                </Box>
              ))}
            </Box>
          )}
        </Box>
        <ConvertedPrice
          {...getProductPriceBounds(product)}
          currency={product.currency}
          sx={{ mt: sized(-0.25, -0.75), mb: sized(0.5, 1) }}
        />
        {product.numReviews > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: sized(0.5, 1),
            }}
          >
            <Rating
              value={product.rating || 0}
              precision={0.1}
              readOnly
              size="small"
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: metaFontSize }}
            >
              ({product.numReviews})
            </Typography>
          </Box>
        )}
        {locationText && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: metaFontSize,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <LocationOnOutlinedIcon sx={{ fontSize: metaIconSize }} />
            <Box
              component="span"
              sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {locationText}
            </Box>
          </Typography>
        )}
        {showShop && shop?.shopName && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: metaFontSize,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: sized(0.25, 0.5),
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <Box
              component="span"
              sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {shop.shopName}
            </Box>
            {shop.isIdentityVerified && <VerifiedBadge size={12} />}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ProductCard;
